<script>
import { makeLogger } from "@/shared/consola";

const logger = makeLogger("PlayerCreator.vue");
</script>

<script setup>
import VCreator from "@/shared/views/vCreator.vue";
import cCreatorStep from "@/shared/components/creator/cCreatorStep.vue";
import cCreatorThemes from "@/shared/components/creator/cCreatorThemes.vue";
import cCreatorProduct from "@/shared/components/creator/cCreatorProduct.vue";
import { ButtonPrimarySm } from "@easy/ui/buttons";
import { RocketIcon } from "@easy/ui/icons";
import { computed } from "vue";
import { isColorDark } from "@/shared/utils/helpers";
import { usePlayerCreatorStore } from "@/apps/player/project/onboarding/player-creator.store";
import { CardSquare, CardsSquare } from "@easy/ui/cards";
import { storeToRefs } from "pinia";
import { playerOnboardingService } from "@/apps/player/project/onboarding/player-onboarding.service";
import { useI18n } from "vue-i18n";
import { useOnboardingVideo } from "@/shared/projects/onboarding/useOnboardingVideos";
import { useAsyncState } from "@vueuse/core";
import { userService } from "@/shared/user/user.service";
import { linksUtil } from "@/shared/utils/router/links";

/*const { player } = useState("player", ["player"]);
const { uploadLogo } = useActions("player", ["uploadLogo"]);*/
const { locale } = useI18n();
const { video } = useOnboardingVideo("player");
const playerCreatorStore = usePlayerCreatorStore();
const { onboardState, previewCssVars, templates, previewComponent } =
    storeToRefs(playerCreatorStore);
const { toggleCreatorShown, onboardingAction, updateProductInfo } = playerCreatorStore;

const backgroundColor = computed(() =>
    isColorDark(onboardState.value.theme?.colors?.background) ? "#f9fafb" : "#17171C"
);

/*const files = ref([]);

async function uploadImage($event) {
    await uploadLogo({ file: $event });
    player.value.pageIcon = player.value.logo;
    files.value = [
        makeFileModel({
            url: player.value.logo,
        }),
    ];
}

function removeImage() {
    player.value.logo = "";
    player.value.pageIcon = "";
    files.value = [];
}*/

const generateOnboarding = onboardingAction(() => {
    logger.debug("send Player onboarding...", locale.value, onboardState.value);
    return playerOnboardingService.sendOnboardData({
        ...onboardState.value,
        theme: onboardState.value.theme,
        lang: locale.value,
    });
});

// Collect tenant data:
useAsyncState(
    () => userService.getTenantDetails(),
    {},
    {
        immediate: true,
        onSuccess(data) {
            const { name = "", websiteUrl = "", companyData } = data || {};
            onboardState.value.companyName = companyData?.companyName || name;
            onboardState.value.website = linksUtil.trimHttp(websiteUrl);
        },
    }
);
</script>

<template>
    <VCreator
        :backColor="backgroundColor"
        :canSkipGenerator="!onboardState.isLoading"
        :description="$t('player.creator.subtitle')"
        :title="$t('player.creator.title')"
        :video="video"
        bigSvgSize
        @close="toggleCreatorShown(false)"
    >
        <cCreatorStep
            :title="
                onboardState.showProductPickerOnly
                    ? $t('shared.creator.step_data_source_title_no_prompt')
                    : $t('shared.creator.step_data_source_title')
            "
            :description="
                onboardState.showProductPickerOnly
                    ? $t('shared.creator.step_data_source_description_no_prompt')
                    : $t('shared.creator.step_data_source_description')
            "
            :step="1"
        >
            <cCreatorProduct
                :error="onboardState.error"
                :hidePrompt="onboardState.hidePrompt"
                :showProductPickerOnly="onboardState.showProductPickerOnly"
                v-model:productSelectLater="onboardState.productSelectLater"
                @productInfoUpdated="updateProductInfo($event)"
            >
                <!-- EAS-2196 - temporary added -->
                <template #templated>
                    <CardsSquare>
                        <CardSquare
                            v-for="template in templates"
                            :key="template.id"
                            :title="template.value"
                            :is-selected="template.id === onboardState.template"
                            @click="
                                onboardState.template = template.id;
                                onboardState.promptDescription = `use:(${onboardState.template})`;
                            "
                        >
                            <component class="w-6 h-6" :is="template.icon" />
                        </CardSquare>
                    </CardsSquare>
                </template>
            </cCreatorProduct>
        </cCreatorStep>
        <!--        <cCreatorStep
            :title="$t('player.creator.step_2_title')"
            :description="$t('player.creator.step_2_description')"
            :step="2"
        >
            <div class="grid lg:grid-cols-3 gap-6">
                <div class="space-y-5">
                    <FormInput
                        v-model="onboardState.companyName"
                        :title="$t('legal.company.company_name')"
                        :placeholder="$t('legal.company.company_name_placeholder')"
                    />
                    <FormInputUrl
                        v-model="onboardState.website"
                        :title="$t('legal.company.internet_address')"
                        placeholder="google.com"
                    />
                </div>
                <FormFiles
                    :title="$t('player.creator.logo')"
                    :labels="{
                        clickToUpload: $t('shared.uploader.click_to_upload'),
                        dragAndDrop: $t('shared.uploader.drag_and_drop'),
                        fileInfo: $t('shared.uploader.file_details'),
                    }"
                    :maxSize="10000"
                    :errorMessages="{
                        invalidSize: $t('shared.invalid.file_size_over'),
                        invalidType: $t('shared.invalid.file_type'),
                    }"
                    :single-file="true"
                    v-model="files"
                    @fileSelected="uploadImage"
                    @fileRemoved="removeImage"
                />
            </div>
        </cCreatorStep>-->
        <cCreatorStep
            :title="$t('player.creator.step_3_title')"
            :description="$t('player.creator.step_3_description')"
            :step="2"
        >
            <cCreatorThemes v-model="onboardState.theme" />
        </cCreatorStep>
        <cCreatorStep
            :title="$t('player.creator.step_4_title')"
            :description="$t('player.creator.step_4_description')"
            :step="3"
            :last="true"
        >
            <ButtonPrimarySm @click="generateOnboarding()" :loading="onboardState.isLoading">
                <RocketIcon />
                <span>{{ $t("player.creator.step_4_cta") }}</span>
            </ButtonPrimarySm>
        </cCreatorStep>
        <template #preview>
            <div :style="{ ...previewCssVars }">
                <component :is="previewComponent" />
            </div>
        </template>
    </VCreator>
</template>
